import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Globals } from "@coreapp/CoreApp/Globals";
import { GetWSMgr } from "@/Utils/StateLib";
import { gWidgets } from "@coreapp/whitelabel/brand";
import { enumWidgetType } from "@coreapp/CoreApp/widgets/widgetTypes";
import { QLoadGuidesRFC } from "@coreapp/CoreApp/QLoadGuidesRFC";
import { QUpdateGuideStateRFC } from "@coreapp/CoreApp/QUpdateGuideStateRFC";
import { QResetGuideStateRFC } from "@coreapp/CoreApp/QResetGuideStateRFC";
import { guideRegistry } from "../guides/guideRegistry";
import { useAppReady } from "@/hooks/useAppReady";
import type { GuideDefinition, GuideRecord, GuideStateEntry } from "../NativeGuideTypes";

interface NativeGuidesContextType {
  guideStates: Record<string, GuideStateEntry>;
  isLoaded: boolean;
  dismissGuide: (guideId: string) => void;
  completeGuide: (guideId: string) => void;
  retriggerGuide: (...guideIds: string[]) => void;
  /** True if the guide has not been dismissed/completed at its current version */
  shouldShowGuide: (guide: GuideDefinition) => boolean;
}

const NativeGuidesContext = createContext<NativeGuidesContextType | null>(null);

const isEnabled = () => enumWidgetType.nativeResourceCenter in gWidgets;

const NativeGuidesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [guideStates, setGuideStates] = useState<Record<string, GuideStateEntry>>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const loadedRef = useRef(false);
  const appReady = useAppReady();

  useEffect(() => {
    if (!appReady || !isEnabled() || loadedRef.current) return;
    loadedRef.current = true;

    try {
      const wsMgr = GetWSMgr();
      if (!wsMgr) {
        loadedRef.current = false;
        return;
      }

      const rfc = new QLoadGuidesRFC();
      rfc.Init({
        HandleGuidesLoaded: (response: { success: boolean; guides: GuideRecord[] }) => {
          if (!response.success) {
            loadedRef.current = false;
            return;
          }
          const stateMap: Record<string, GuideStateEntry> = {};
          for (const { guideId, version, state } of response.guides) {
            stateMap[guideId] = { guideId, version, state };
          }
          setGuideStates(stateMap);
          setIsLoaded(true);
        }
      });
      rfc.SetParams(wsMgr.GetAccountName(), wsMgr.GetUserName());
      Globals._rfcMgr.QueueRFC(rfc);
    } catch (e) {
      console.error("[NativeGuidesProvider] Failed to load guide states:", e);
      loadedRef.current = false;
    }
  }, [appReady]);

  const persistState = useCallback((guideId: string, state: "dismissed" | "completed") => {
    const wsMgr = GetWSMgr();
    if (!wsMgr) return;

    const guide = guideRegistry[guideId];
    if (!guide) return;

    setGuideStates(prev => ({
      ...prev,
      [guideId]: { guideId, state, version: guide.version }
    }));

    const rfc = new QUpdateGuideStateRFC();
    rfc.Init(null);
    rfc.SetParams(wsMgr.GetAccountName(), wsMgr.GetUserName(), guideId, guide.version, state);
    Globals._rfcMgr.QueueRFC(rfc);
  }, []);

  const dismissGuide = useCallback(
    (guideId: string) => persistState(guideId, "dismissed"),
    [persistState]
  );
  const completeGuide = useCallback(
    (guideId: string) => persistState(guideId, "completed"),
    [persistState]
  );
  const retriggerGuide = useCallback((...guideIds: string[]) => {
    if (!guideIds.length) return;
    const wsMgr = GetWSMgr();
    if (!wsMgr) return;

    const rfc = new QResetGuideStateRFC();
    rfc.Init({
      HandleGuideStateReset: (response: { success: boolean }) => {
        if (!response.success) return;
        setGuideStates(prev => {
          const next = { ...prev };
          for (const id of guideIds) delete next[id];
          return next;
        });
      }
    });
    rfc.SetParams(wsMgr.GetAccountName(), wsMgr.GetUserName(), guideIds);
    Globals._rfcMgr.QueueRFC(rfc);
  }, []);

  const shouldShowGuide = useCallback(
    (guide: GuideDefinition): boolean => {
      const record = guideStates[guide.id];
      if (!record) return true;
      // Both dismissed and completed suppress until version bumps
      return record.version < guide.version;
    },
    [guideStates]
  );

  if (!isEnabled()) return null;

  return (
    <NativeGuidesContext.Provider
      value={{
        guideStates,
        isLoaded,
        dismissGuide,
        completeGuide,
        retriggerGuide,
        shouldShowGuide
      }}
    >
      {children}
    </NativeGuidesContext.Provider>
  );
};

export default NativeGuidesProvider;

export const useNativeGuides = (): NativeGuidesContextType => {
  const ctx = useContext(NativeGuidesContext);
  if (!ctx) throw new Error("useNativeGuides must be used within NativeGuidesProvider");
  return ctx;
};
